// ─── marketplaceShares.js ─────────────────────────────────────────────────────
// Pure share-recompute helpers used by both the Marketplaces page UI and the
// adapter pipeline (src/lib/marketplaceAdapters.js). No React imports — safe
// to call from anywhere, including server-side or test environments.
//
// These were originally living in src/pages/marketplaces/marketplaceUtils.js
// but adapters need them too, so they were promoted up to src/lib/. The page
// still re-exports them from the original path for backwards compatibility.
//
// Rules enforced here:
//
//   1. A marketplace is "active" if it has products, a non-zero pctOfTotal,
//      or a connected API. Inactive marketplaces stay at pctOfTotal = 0.
//
//   2. Single active marketplace → pctOfTotal = 1.0 (100% of brand)
//      (unless the user has manually overridden it via _overridden.pctOfTotal)
//
//   3. Multiple active marketplaces → distribute 100% by _recentSalesVolume
//      proportionally. Manual overrides absorb their share first; the rest
//      split the leftover.
//
//   4. Same logic at the product level via recomputeProductShares — except
//      decoupled products are excluded from both numerator and denominator
//      and forced to pctOfMarket = 0.

export function isOverridden(entity, field) {
  return !!(entity?._overridden && entity._overridden[field]);
}

export function getApiValue(entity, field) {
  return entity?._apiSnapshot ? entity._apiSnapshot[field] : undefined;
}

export function applyOverride(entity, field, value) {
  return {
    ...entity,
    [field]: value,
    _overridden: { ...(entity._overridden || {}), [field]: true },
  };
}

export function resetField(entity, field) {
  const apiVal = getApiValue(entity, field);
  if (apiVal === undefined) return entity;
  const newOver = { ...(entity._overridden || {}) };
  delete newOver[field];
  return { ...entity, [field]: apiVal, _overridden: newOver };
}

// Apply API-synced data to an entity:
// - writes the new value
// - caches the value in _apiSnapshot for "reset to API"
// - SKIPS the field entirely if the user has overridden it
//   (so a re-sync never wipes manual edits)
export function applyApiPatch(entity, patch) {
  const out = { ...entity };
  const snap = { ...(entity._apiSnapshot || {}) };
  for (const [field, value] of Object.entries(patch)) {
    snap[field] = value;
    if (!isOverridden(entity, field)) {
      out[field] = value;
    }
  }
  out._apiSnapshot = snap;
  return out;
}

// Read the recent-sales-volume off an item, accepting either the persisted
// `recentSalesVolume` field (set by loadBrand) or the legacy in-memory
// `_recentSalesVolume` (set by adapters during a sync). Without this fallback,
// recomputes that run after a page reload would see only the marketplace
// most-recently-synced this session and zero out everything else.
function readVolume(item) {
  if (!item) return 0;
  const v = item.recentSalesVolume ?? item._recentSalesVolume;
  return Math.max(0, Number(v) || 0);
}

// ── Generic share recompute ──────────────────────────────────────────────────
// Given a list of items (each with a sales volume), distribute 100% across
// them honoring any manual overrides. The same helper is used at the
// marketplace level (valueField = "pctOfTotal", volume = mkt.recentSalesVolume)
// and at the product level (valueField = "pctOfMarket", volume = product.recentSalesVolume).
export function recomputeShares(items, valueField = "pctOfTotal", pctField = "pct", volumeField = "recentSalesVolume") {
  if (!items || items.length === 0) return items;

  const overridden = items.filter(it => isOverridden(it, valueField));
  const auto = items.filter(it => !isOverridden(it, valueField));

  const lockedShare = overridden.reduce((s, it) => s + (Number(it[valueField]) || 0), 0);
  const cappedLocked = Math.min(1, lockedShare);
  const remaining = Math.max(0, 1 - cappedLocked);

  const autoVolumes = auto.map(readVolume);
  const totalVol = autoVolumes.reduce((s, v) => s + v, 0);

  let result;
  if (totalVol > 0) {
    result = items.map(it => {
      if (isOverridden(it, valueField)) return it;
      const vol = readVolume(it);
      const share = remaining * (vol / totalVol);
      return { ...it, [valueField]: share };
    });
  } else if (auto.length > 0) {
    // No volume data at all. Instead of equal-splitting (which wipes out
    // existing shares when a new empty marketplace is added), PRESERVE the
    // existing pctOfTotal values if they sum to something meaningful. Only
    // fall back to equal split if the existing values are all 0.
    const existingSum = auto.reduce((s, it) => s + (Number(it[valueField]) || 0), 0);
    if (existingSum > 0) {
      // Redistribute the remaining share proportionally to existing values
      result = items.map(it => {
        if (isOverridden(it, valueField)) return it;
        const existing = Number(it[valueField]) || 0;
        return { ...it, [valueField]: remaining * (existing / existingSum) };
      });
    } else {
      const share = remaining / auto.length;
      result = items.map(it => {
        if (isOverridden(it, valueField)) return it;
        return { ...it, [valueField]: share };
      });
    }
  } else {
    result = items;
  }
  return result;
}

// ── Marketplace activity check ──────────────────────────────────────────────
// A marketplace is "active" (worthy of being included in % share calc) if it
// either has products or has been manually configured.
export function isMarketplaceActive(mkt) {
  if (!mkt) return false;
  // A marketplace is active only if it has non-decoupled products.
  // API connection alone is not enough — no products means no income to share.
  if ((mkt.products || []).filter(p => !p._decoupled).length > 0) return true;
  return false;
}

// ── Recompute marketplace shares ─────────────────────────────────────────────
// Top-level: recompute every marketplace's pctOfTotal. Inactive marketplaces
// stay at 0. Active marketplaces split 100% by recent sales volume (with
// manual overrides honored).
export function recomputeMarketplaceShares(marketplaces) {
  if (!marketplaces || marketplaces.length === 0) return marketplaces;
  const active = marketplaces.filter(isMarketplaceActive);

  if (active.length === 0) return marketplaces;
  if (active.length === 1) {
    // Single active marketplace gets 100% (unless manually overridden)
    return marketplaces.map(m => {
      if (!isMarketplaceActive(m)) return { ...m, pctOfTotal: 0 };
      if (isOverridden(m, "pctOfTotal")) return m;
      return { ...m, pctOfTotal: 1 };
    });
  }

  // Multiple active — distribute by volume
  const recomputed = recomputeShares(active, "pctOfTotal");
  // Merge back with inactive
  const map = Object.fromEntries(recomputed.map(m => [m.id, m]));
  return marketplaces.map(m => {
    if (map[m.id]) return map[m.id];
    return { ...m, pctOfTotal: 0 };
  });
}

// ── Recompute product shares within a marketplace ────────────────────────────
// Each non-decoupled product gets its share of 100%. Decoupled products
// (`_decoupled: true`) are excluded from both numerator and denominator —
// they don't show up in the income calc at all.
export function recomputeProductShares(products) {
  if (!products || products.length === 0) return products;

  const active = products.filter(p => !p._decoupled);

  const recomputed = recomputeShares(active, "pctOfMarket", "pctOfMarket");
  const map = Object.fromEntries(recomputed.map(p => [p.id, p]));
  return products.map(p => {
    if (p._decoupled) return { ...p, pctOfMarket: 0 };
    return map[p.id] || p;
  });
}
